(function () {
  'use strict';

  var DEFAULT_MAX_MILES = 15;
  var CACHE_KEY = 'scanGeoCache_v8';
  var DEBOUNCE_MS = 450;
  var MIN_GEO_CHARS = 3;
  var MIN_TEXT_CHARS = 2;

  /* ── Cache ── */
  function loadCache() { try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || {}; } catch (e) { return {}; } }
  function saveCache(c) { try { localStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch (e) {} }
  var GEO_CACHE = loadCache();

  /* ── Single geocode call (free-text search) ── */
  function fetchCoords(query) {
    var key = query.toLowerCase().trim();
    if (GEO_CACHE[key]) return Promise.resolve(GEO_CACHE[key]);
    return fetch(
      'https://nominatim.openstreetmap.org/search?format=json&limit=1&q=' + encodeURIComponent(query),
      { headers: { Accept: 'application/json' } }
    )
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data || !data.length) return null;
        var result = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        GEO_CACHE[key] = result;
        saveCache(GEO_CACHE);
        return result;
      })
      .catch(function () { return null; });
  }

  /*
   * Structured postcode/zip lookup. Nominatim's dedicated `postalcode`
   * parameter is far more reliable than stuffing a bare postcode into
   * the general free-text `q` search.
   */
  function fetchStructuredPostcode(postcode, countryHint) {
    var normalized = normalizePostcode(postcode);
    var key = ('pc:' + normalized + '|' + (countryHint || '')).toLowerCase().trim();
    if (GEO_CACHE[key]) return Promise.resolve(GEO_CACHE[key]);

    var url = 'https://nominatim.openstreetmap.org/search?format=json&limit=1&postalcode=' + encodeURIComponent(normalized);
    if (countryHint) url += '&country=' + encodeURIComponent(countryHint);

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data || !data.length) return null;
        var result = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        GEO_CACHE[key] = result;
        saveCache(GEO_CACHE);
        return result;
      })
      .catch(function () { return null; });
  }

  function normalizePostcode(q) {
    var s = q.trim();
    /* UK outward+inward: "SW1A 1AA" → keep single space */
    var ukMatch = s.match(/^([A-Za-z]{1,2}\d[A-Za-z0-9]?)\s*(\d[A-Za-z]{2})$/);
    if (ukMatch) return (ukMatch[1] + ' ' + ukMatch[2]).toUpperCase();
    return s.replace(/\s+/g, '');
  }

  /*
   * Heuristic: does the typed query look like a postcode/zip rather than
   * a place name? Covers UK-style alphanumeric postcodes (e.g. "SW1A 1AA")
   * and numeric zip codes (e.g. Pakistan's "54000", "54792").
   */
  function isLikelyPostcode(q) {
    var s = q.replace(/\s+/g, '');
    if (/^[0-9]{4,6}$/.test(s)) return true;
    if (/^[A-Za-z]{1,2}[0-9][A-Za-z0-9]?[0-9][A-Za-z]{2}$/.test(s)) return true;
    return false;
  }

  function uniqueQueries(list) {
    return list.filter(function (q, i) {
      return q && list.indexOf(q) === i;
    });
  }

  function buildSearchQueries(query, countryHint) {
    var q = query.trim();
    var hint = (countryHint || '').trim();
    var queries = [];

    if (hint) queries.push(q + ', ' + hint);
    queries.push(q);

    return uniqueQueries(queries);
  }

  function runAttemptChain(attempts) {
    return attempts.reduce(function (promise, attempt) {
      return promise.then(function (result) {
        if (result) return result;
        return attempt();
      });
    }, Promise.resolve(null));
  }

  function runQueryChain(queries) {
    return runAttemptChain(queries.map(function (q) {
      return function () { return fetchCoords(q); };
    }));
  }

  function geocodeUserSearch(query, countryHint) {
    var q = query.trim();
    var hint = (countryHint || '').trim();
    var attempts = [];

    if (isLikelyPostcode(q)) {
      if (hint) attempts.push(function () { return fetchStructuredPostcode(q, hint); });
      attempts.push(function () { return fetchStructuredPostcode(q, ''); });
    }

    buildSearchQueries(q, hint).forEach(function (textQuery) {
      attempts.push(function () { return fetchCoords(textQuery); });
    });

    return runAttemptChain(attempts);
  }

  function reverseGeocodeCountry(lat, lng) {
    return fetch(
      'https://nominatim.openstreetmap.org/reverse?format=json&zoom=3&lat=' + lat + '&lon=' + lng,
      { headers: { Accept: 'application/json' } }
    )
      .then(function (r) { return r.json(); })
      .then(function (data) {
        return (data && data.address && data.address.country) || '';
      })
      .catch(function () { return ''; });
  }

  function geocodeClinicAddress(address, city, countryHint) {
    var base = [];
    if (address && city) base.push(address + ', ' + city);
    if (city) base.push(city);
    if (address) base.push(address);

    var hint = (countryHint || '').trim();
    var withCountry = [];
    if (hint) {
      base.forEach(function (q) {
        withCountry.push(q + ', ' + hint);
      });
    }

    return runQueryChain(uniqueQueries(base.concat(withCountry)));
  }

  /* ── Distance in miles ── */
  function getMiles(lat1, lng1, lat2, lng2) {
    var R = 6371;
    var p = Math.PI / 180;
    var dLat = (lat2 - lat1) * p;
    var dLng = (lng2 - lng1) * p;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * p) * Math.cos(lat2 * p) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 0.621371;
  }

  /* ── Text search against clinic metaobject fields ── */
  function getClinicSearchText(slide) {
    var parts = [
      slide.getAttribute('data-clinic-name') || '',
      slide.getAttribute('data-full-address') || '',
      slide.getAttribute('data-city') || ''
    ];
    var nameInput = slide.querySelector('[data-clinic-name]');
    if (nameInput && !parts[0]) {
      parts[0] = nameInput.getAttribute('data-clinic-name') || '';
    }
    return parts.join(' ').toLowerCase();
  }

  function clinicMatchesText(slide, query) {
    var q = (query || '').toLowerCase().trim();
    if (!q || q.length < MIN_TEXT_CHARS) return false;

    var text = getClinicSearchText(slide);
    if (text.indexOf(q) !== -1) return true;

    var qWords = q.split(/\s+/).filter(function (w) { return w.length >= MIN_TEXT_CHARS; });
    if (qWords.length > 1 && qWords.every(function (w) { return text.indexOf(w) !== -1; })) {
      return true;
    }

    var qCompact = q.replace(/\s+/g, '');
    var textCompact = text.replace(/\s+/g, '');
    return qCompact.length >= MIN_TEXT_CHARS && textCompact.indexOf(qCompact) !== -1;
  }

  function getClinicCoords(slide, countryHint) {
    var lat = parseFloat(slide.getAttribute('data-lat'));
    var lng = parseFloat(slide.getAttribute('data-lng'));
    if (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
      return Promise.resolve({ lat: lat, lng: lng });
    }

    var fullAddress = slide.getAttribute('data-full-address') || '';
    var city = slide.getAttribute('data-city') || '';

    return geocodeClinicAddress(fullAddress, city, countryHint).then(function (coords) {
      if (coords) {
        slide.setAttribute('data-lat', coords.lat);
        slide.setAttribute('data-lng', coords.lng);
      }
      return coords;
    });
  }

  function getPanelMaxMiles(panel) {
    var miles = parseFloat(panel.getAttribute('data-max-distance-miles'));
    return !isNaN(miles) && miles > 0 ? miles : DEFAULT_MAX_MILES;
  }

  function refreshCarousel(container) {
    if (window.ScanCenterSlider && container) {
      window.ScanCenterSlider.destroy(container);
      window.ScanCenterSlider.reinit(container);
    }
  }

  function renderClinicResults(panel, results, label, maxMiles, isCurrent, onDone) {
    var container = panel.closest('[data-treatment-start]');
    var slider = panel.querySelector('[data-scan-center-slider]');
    var noResults = panel.querySelector('[data-scan-center-no-results]');
    var noteEl = panel.querySelector('[data-scan-center-distance-note]');

    if (isCurrent && !isCurrent()) return;

    var visible = results.filter(function (r) { return r.visible; });
    var hidden = results.filter(function (r) { return !r.visible; });

    visible.sort(function (a, b) {
      if (a.textMatch !== b.textMatch) return a.textMatch ? -1 : 1;
      return a.dist - b.dist;
    });

    if (window.ScanCenterSlider && container) {
      window.ScanCenterSlider.destroy(container);
    }

    visible.forEach(function (item) {
      item.slide.classList.remove('hidden-by-search');
      slider.appendChild(item.slide);
    });
    hidden.forEach(function (item) {
      item.slide.classList.add('hidden-by-search');
      slider.appendChild(item.slide);
    });

    refreshCarousel(container);
    if (window.ScanCenterSlider && container) {
      window.ScanCenterSlider.applyDistanceFilter(container);
    }

    if (noResults) noResults.classList.toggle('hidden', visible.length > 0);
    if (noteEl) {
      if (visible.length > 0) {
        var textHits = visible.filter(function (r) { return r.textMatch; }).length;
        var geoHits = visible.filter(function (r) { return r.geoMatch; }).length;
        var parts = [];

        if (textHits) parts.push(textHits + ' matching "' + label + '"');
        if (geoHits && label !== 'your location') {
          parts.push(geoHits + ' within ' + maxMiles + ' miles');
        } else if (label === 'your location') {
          parts.push(visible.length + ' within ' + maxMiles + ' miles of ' + label);
        }

        noteEl.textContent = parts.length ? parts.join(' · ') + '.' : visible.length + ' clinic' + (visible.length > 1 ? 's' : '') + ' found.';
      } else {
        noteEl.textContent = '';
      }
      noteEl.classList.toggle('hidden', visible.length === 0);
    }

    if (onDone) onDone(visible.length);
  }

  /*
   * Combined search: text matches (name / address / city / postcode in address)
   * plus clinics within maxMiles of a geocoded origin.
   * Works for UK postcodes, Pakistan zips, city names, and clinic names.
   */
  function searchClinicsCombined(panel, options) {
    var textQuery = (options.textQuery || '').trim();
    var originLat = options.originLat;
    var originLng = options.originLng;
    var label = options.label || textQuery;
    var countryHint = options.countryHint || '';
    var onDone = options.onDone;
    var isCurrent = options.isCurrent;

    var container = panel.closest('[data-treatment-start]');
    var slides = Array.prototype.slice.call(panel.querySelectorAll('.scan-center-slide'));
    var maxMiles = getPanelMaxMiles(panel);
    var hasOrigin = typeof originLat === 'number' && typeof originLng === 'number' && !isNaN(originLat) && !isNaN(originLng);

    slides.forEach(function (slide) {
      slide.classList.remove('hidden-by-search');
    });

    /* Fast path: text-only filter — no clinic geocoding on every keystroke */
    if (!hasOrigin && textQuery) {
      var textResults = slides.map(function (slide) {
        var textMatch = clinicMatchesText(slide, textQuery);
        var distEl = slide.querySelector('[data-scan-center-card-distance]');
        if (distEl) distEl.textContent = '';
        return {
          slide: slide,
          dist: Infinity,
          textMatch: textMatch,
          geoMatch: false,
          visible: textMatch
        };
      });
      renderClinicResults(panel, textResults, label, maxMiles, isCurrent, onDone);
      return;
    }

    Promise.all(slides.map(function (slide) {
      return getClinicCoords(slide, countryHint).then(function (coords) {
        var distEl = slide.querySelector('[data-scan-center-card-distance]');
        var textMatch = textQuery ? clinicMatchesText(slide, textQuery) : false;
        var geoMatch = false;
        var dist = Infinity;

        if (coords && hasOrigin) {
          dist = getMiles(originLat, originLng, coords.lat, coords.lng);
          geoMatch = dist <= maxMiles;
        }

        var visible;
        if (hasOrigin && textQuery) {
          visible = textMatch || geoMatch;
        } else if (hasOrigin) {
          visible = geoMatch;
        } else if (textQuery) {
          visible = textMatch;
        } else {
          visible = true;
        }

        if (distEl) {
          if (visible && hasOrigin && coords && dist !== Infinity) {
            distEl.textContent = dist.toFixed(1) + ' miles away';
          } else if (visible && textMatch && !hasOrigin) {
            distEl.textContent = '';
          } else {
            distEl.textContent = '';
          }
        }

        return { slide: slide, dist: dist, textMatch: textMatch, geoMatch: geoMatch, visible: visible };
      });
    })).then(function (results) {
      renderClinicResults(panel, results, label, maxMiles, isCurrent, onDone);
    });
  }

  /* GPS / single-origin filter (unchanged behaviour) */
  function filterClinics(panel, originLat, originLng, label, countryHint, onDone, isCurrent) {
    searchClinicsCombined(panel, {
      originLat: originLat,
      originLng: originLng,
      label: label,
      countryHint: countryHint,
      onDone: onDone,
      isCurrent: isCurrent
    });
  }

  function resetPanel(panel, originalSlides) {
    var container = panel.closest('[data-treatment-start]');
    var slider = panel.querySelector('[data-scan-center-slider]');
    var slides = originalSlides && originalSlides.length
      ? originalSlides
      : Array.prototype.slice.call(panel.querySelectorAll('.scan-center-slide'));

    slides.forEach(function (s) {
      s.classList.remove('hidden-by-search');
      var d = s.querySelector('[data-scan-center-card-distance]');
      if (d) d.textContent = '';
      if (slider) slider.appendChild(s);
    });

    ['[data-scan-center-no-results]', '[data-scan-center-distance-note]', '[data-scan-center-locate-error]'].forEach(function (sel) {
      var el = panel.querySelector(sel);
      if (el) {
        el.textContent = '';
        el.classList.add('hidden');
      }
    });

    refreshCarousel(container);
  }

  function initPanel(panel) {
    var wrapper = panel.querySelector('[data-scan-center-search-wrapper]');
    var input = panel.querySelector('[data-scan-center-search]');
    var clearBtn = panel.querySelector('[data-scan-center-clear-btn]');
    var icons = panel.querySelector('[data-scan-center-status-icon]');
    var errEl = panel.querySelector('[data-scan-center-locate-error]');
    var slider = panel.querySelector('[data-scan-center-slider]');
    var geocodeCountry = panel.getAttribute('data-geocode-country') || '';
    var derivedCountry = '';
    var derivedCountryPromise = null;
    var originalSlides = slider
      ? Array.prototype.slice.call(slider.querySelectorAll('.scan-center-slide'))
      : [];

    if (!input) return;

    function ensureCountryHint() {
      if (geocodeCountry) return Promise.resolve(geocodeCountry);
      if (derivedCountry) return Promise.resolve(derivedCountry);
      if (derivedCountryPromise) return derivedCountryPromise;

      var firstSlide = originalSlides[0];
      if (!firstSlide) return Promise.resolve('');

      derivedCountryPromise = getClinicCoords(firstSlide, '').then(function (coords) {
        if (!coords) return '';
        return reverseGeocodeCountry(coords.lat, coords.lng).then(function (country) {
          derivedCountry = country;
          return country;
        });
      });

      return derivedCountryPromise;
    }

    var iconLocate = icons && icons.querySelector('.icon-locate');
    var iconLoading = icons && icons.querySelector('.icon-loading');
    var iconSearch = icons && icons.querySelector('.icon-search');

    var gpsAttempted = false;
    var manualMode = false;
    var searchToken = 0;
    var debounceTimer = null;

    // function showIcon(n) {
    //   [iconLocate, iconLoading, iconSearch].forEach(function (el) {
    //     if (el) el.classList.add('hidden');
    //   });
    //   if (n === 'locate' && iconLocate) iconLocate.classList.remove('hidden');
    //   if (n === 'loading' && iconLoading) iconLoading.classList.remove('hidden');
    //   if (n === 'search' && iconSearch) iconSearch.classList.remove('hidden');
    // }

    function showIcon(n) {
      [iconLocate, iconLoading, iconSearch].forEach(function (el) {
        if (el) el.classList.add('hidden');
      });
      if (n === 'locate' && iconLocate) iconLocate.classList.remove('hidden');
      if (n === 'loading' && iconLoading) iconLoading.classList.remove('hidden');
      if (n === 'search' && iconSearch) {
        var clearVisible = clearBtn && !clearBtn.classList.contains('hidden');
        if (!clearVisible) iconSearch.classList.remove('hidden');
      }
    }

    function setErr(msg) {
      if (!errEl) return;
      errEl.textContent = msg || '';
      errEl.classList.toggle('hidden', !msg);
    }

    // function showClear(yes) {
    //   if (clearBtn) clearBtn.classList.toggle('hidden', !yes);
    // }


    function showClear(yes) {
  if (clearBtn) clearBtn.classList.toggle('hidden', !yes);

  if (iconSearch) {
    iconSearch.classList.toggle('hidden', yes);
  }
}

    function cancelDebounce() {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
    }

    function enterManualMode(placeholder) {
      manualMode = true;
      gpsAttempted = false;
      searchToken++;
      cancelDebounce();
      input.readOnly = false;
      input.placeholder = placeholder || 'Type postcode, city, or clinic name...';
      showIcon('search');
      setErr('');
    }

    function fullReset() {
      gpsAttempted = false;
      manualMode = false;
      searchToken++;
      cancelDebounce();
      input.value = '';
      input.readOnly = false;
      input.placeholder = 'Click here to find nearby clinics...';
      showIcon('locate');
      showClear(false);
      setErr('');
      resetPanel(panel, originalSlides);
    }

    function triggerGPS() {
      if (gpsAttempted || manualMode || input.value.trim()) return;
      gpsAttempted = true;
      var myToken = ++searchToken;

      if (!('geolocation' in navigator)) {
        enterManualMode('Location not supported — type your postcode...');
        setErr('Location not supported by your browser. Please type your postcode or city.');
        return;
      }

      showIcon('loading');
      input.placeholder = 'Detecting your location...';
      input.readOnly = true;
      setErr('');

      navigator.geolocation.getCurrentPosition(
        function (pos) {
          if (myToken !== searchToken || manualMode) return;

          var userLat = pos.coords.latitude;
          var userLng = pos.coords.longitude;
          input.placeholder = 'Finding clinics near you...';

          filterClinics(panel, userLat, userLng, 'your location', geocodeCountry, function () {
            if (myToken !== searchToken || manualMode) return;
            input.readOnly = false;
            input.placeholder = 'Near you — or type a postcode, city, or clinic...';
            showIcon('locate');
            showClear(true);
          }, function () { return myToken === searchToken && !manualMode; });
        },
        function (err) {
          if (myToken !== searchToken || manualMode) return;
          gpsAttempted = false;
          fullReset();
          if (err.code === 1) {
            enterManualMode('Location denied — type your postcode or city...');
            setErr('Location access denied. Type your postcode, city, or clinic name.');
          } else {
            enterManualMode('Could not detect location — type your postcode or city...');
            setErr('Could not get your location. Please type your postcode or city instead.');
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }

    function runTextSearch(query, myToken) {
      if (query.length < MIN_TEXT_CHARS) return;

      searchClinicsCombined(panel, {
        textQuery: query,
        label: query,
        countryHint: geocodeCountry,
        isCurrent: function () { return myToken === searchToken; }
      });
    }

    function runGeocodeSearch(query, myToken) {
      if (myToken !== searchToken || query.length < MIN_GEO_CHARS) return;

      showIcon('loading');
      input.placeholder = 'Searching near "' + query + '"...';

      ensureCountryHint().then(function (hint) {
        if (myToken !== searchToken) return;
        return geocodeUserSearch(query, hint);
      }).then(function (coords) {
        if (myToken !== searchToken) return;
        showIcon('search');

        if (!coords) {
          var hasTextResults = originalSlides.some(function (slide) {
            return clinicMatchesText(slide, query);
          });
          if (!hasTextResults) {
            setErr('"' + query + '" not found. Try a fuller address, postcode, or clinic name.');
          } else {
            setErr('');
          }
          input.placeholder = 'Showing name matches — try a fuller address for nearby clinics...';
          return;
        }

        setErr('');
        searchClinicsCombined(panel, {
          textQuery: query,
          originLat: coords.lat,
          originLng: coords.lng,
          label: query,
          countryHint: geocodeCountry,
          isCurrent: function () { return myToken === searchToken; },
          onDone: function () {
            if (myToken !== searchToken) return;
            input.placeholder = 'Near "' + query + '" — keep typing to refine...';
          }
        });
      }).catch(function () {
        if (myToken !== searchToken) return;
        showIcon('search');
        setErr('Search failed. Check your connection and try again.');
      });
    }

    /*
     * Live + manual search:
     * 1. Instant text filter on clinic name / address / city
     * 2. Debounced geocode → show clinics within 15 miles of typed location
     */
    function runSearch(immediate) {
      var query = input.value.trim();

      if (!query) {
        fullReset();
        return;
      }

      var myToken = ++searchToken;
      setErr('');
      showClear(true);
      showIcon('search');

      runTextSearch(query, myToken);

      if (query.length < MIN_GEO_CHARS) {
        input.placeholder = 'Keep typing postcode, city, or clinic name...';
        return;
      }

      cancelDebounce();
      if (immediate) {
        runGeocodeSearch(query, myToken);
      } else {
        debounceTimer = setTimeout(function () {
          runGeocodeSearch(query, myToken);
        }, DEBOUNCE_MS);
      }
    }

    if (wrapper) {
      wrapper.addEventListener('click', function (e) {
        if (clearBtn && clearBtn.contains(e.target)) return;
        if (!manualMode && !gpsAttempted && !input.value.trim()) triggerGPS();
      });
    }

    input.addEventListener('focus', function () {
      if (!manualMode && !gpsAttempted && !input.value.trim()) triggerGPS();
    });

    input.addEventListener('keydown', function (e) {
      if (!manualMode && e.key !== 'Tab' && e.key !== 'Escape' && e.key !== 'Enter') {
        enterManualMode();
        input.readOnly = false;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        if (!manualMode) enterManualMode();
        runSearch(true);
      }

      if (e.key === 'Escape') {
        fullReset();
      }
    });

    input.addEventListener('input', function () {
      var val = input.value;
      showClear(val.trim().length > 0);

      if (!manualMode && val.trim().length > 0) {
        enterManualMode();
        input.readOnly = false;
      }

      if (!val.trim()) {
        fullReset();
        return;
      }

      runSearch(false);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        fullReset();
      });
    }

    setTimeout(function () {
      originalSlides.forEach(function (slide) {
        getClinicCoords(slide, geocodeCountry);
      });
    }, 1000);

    // showIcon('locate');
    showIcon('search');
  }

  function init() {
    document.querySelectorAll('[data-scan-center-panel]').forEach(initPanel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


